/**
 * Temperature unit preference (Celsius vs Fahrenheit).
 *
 * The user's choice lives in localStorage so it persists across sessions
 * on the same device. When no explicit choice is set, we infer a sensible
 * default from the active market (LocaleContext) — US (and the small set
 * of other Fahrenheit-using countries) defaults to F, everywhere else
 * to C. Components subscribe to changes via the `useTempUnit` hook.
 *
 * Kept client-only on purpose: avoids a DB column / API roundtrip for a
 * pure display preference. The country-based default keeps a fresh
 * device showing sensible units before the user opens Settings.
 */
import { useEffect, useState } from "react";

export type TempUnit = "C" | "F";

const STORAGE_KEY = "bac:temp-unit";
const CHANGE_EVENT = "bac:temp-unit-change";

// Markets where Fahrenheit is the everyday unit. Source: the small set of
// countries that haven't migrated to Celsius (US + a handful of dependencies).
// Only `us` is a configured market today; the others stay future-proof.
const FAHRENHEIT_MARKETS = new Set(["us", "pr", "bs", "bz", "ky"]);

export function getDefaultTempUnit(market?: string | null): TempUnit {
  if (market && FAHRENHEIT_MARKETS.has(market.toLowerCase())) return "F";
  return "C";
}

export function readStoredTempUnit(): TempUnit | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "C" || v === "F" ? v : null;
  } catch {
    return null;
  }
}

export function setStoredTempUnit(unit: TempUnit): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, unit);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: unit }));
  } catch {
    /* ignore — fallback to default */
  }
}

/**
 * Resolved temperature unit + setter. Returns the user's stored choice if
 * present, otherwise the country-based default. Re-renders subscribers
 * when the value changes anywhere in the app (custom event) or in another
 * tab (storage event).
 */
export function useTempUnit(market?: string | null): {
  unit: TempUnit;
  isExplicit: boolean;
  setUnit: (u: TempUnit) => void;
} {
  const [stored, setStored] = useState<TempUnit | null>(() => readStoredTempUnit());

  useEffect(() => {
    function onChange(e: Event) {
      const next = (e as CustomEvent<TempUnit>).detail;
      setStored(next === "C" || next === "F" ? next : readStoredTempUnit());
    }
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      setStored(e.newValue === "C" || e.newValue === "F" ? e.newValue : null);
    }
    window.addEventListener(CHANGE_EVENT, onChange as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const unit = stored ?? getDefaultTempUnit(market);
  return { unit, isExplicit: stored !== null, setUnit: setStoredTempUnit };
}

/**
 * Format a Celsius value for display in the user's chosen unit.
 * Rounded to the nearest whole degree (matches the iOS Weather widget).
 */
export function formatTemperature(c: number | null | undefined, unit: TempUnit): string {
  if (c === null || c === undefined || Number.isNaN(c)) return "—";
  const v = unit === "F" ? (c * 9) / 5 + 32 : c;
  return `${Math.round(v)}°`;
}
